import type { AlbumRecord } from '../types'
import {
  THEME_DEFINITIONS,
  formatCurrency,
  sequenceTarget,
  daysUntil,
  hasInvestmentThisMonth,
  stickerValue,
  themeDisplayName
} from '../data'
import { useI18n } from '../i18n/context'

interface Props {
  record: AlbumRecord
  onSelect: () => void
  onDelete: () => void
}

export function AlbumCard({ record, onSelect, onDelete }: Props): JSX.Element {
  const { t } = useI18n()
  const theme = THEME_DEFINITIONS[record.config.theme]
  const accent = '#f97316'
  const accentSoft = '#ffedd5'
  const stickerCount = Object.keys(record.stickers).length
  const investedAmount = Object.values(record.stickers).reduce(
    (sum, s) => sum + stickerValue(s.stickerNumber, record.config.size),
    0
  )
  const target = sequenceTarget(record.config.size)
  const progress = target > 0 ? investedAmount / target : 0
  const days = daysUntil(record.config.targetFinishDate)
  const activeThisMonth = hasInvestmentThisMonth(record.stickers)

  function daysLabel(): string | null {
    if (days === null) return null
    if (days < 0) return t.daysOverdue(Math.abs(days))
    if (days === 0) return t.targetToday
    return t.daysRemaining(days)
  }

  return (
    <article
      className="album-card"
      style={{ '--accent': accent, '--accent-soft': accentSoft } as React.CSSProperties}
    >
      <div className="album-card-header">
        <div>
          <p className="eyebrow">{themeDisplayName(theme, t)}</p>
          <h2>{record.config.name}</h2>
        </div>
        <div className="album-card-status">
          <span
            className={`status-dot ${activeThisMonth ? 'is-green' : 'is-red'}`}
            title={activeThisMonth ? t.activeMonth : t.noActivityYet}
          />
        </div>
      </div>

      <div className="album-card-meta">
        <span>
          {record.config.size} {t.stickersLabel}
        </span>
        <span>
          {stickerCount} {t.collected.toLowerCase()} ·{' '}
          {formatCurrency(investedAmount, record.config.currency)}
        </span>
        {daysLabel() && (
          <span className={days !== null && days < 0 ? 'text-overdue' : ''}>{daysLabel()}</span>
        )}
      </div>

      <div className="meter">
        <div className="meter-fill" style={{ width: `${Math.min(progress * 100, 100)}%` }} />
      </div>

      <div className="album-card-actions">
        <button className="primary-button" onClick={onSelect} type="button">
          {t.openAlbum}
        </button>
        <button className="ghost-button danger" onClick={onDelete} type="button">
          {t.deleteAlbum}
        </button>
      </div>
    </article>
  )
}
